// actions.ts
import { Context, ISaveConceptNotePayload, IActions, IWorkflowValidationPayload } from './actions.types'
import { RootState } from '@/store/types'
import { IWorkspaceData, Notification, INotification, Application, IApplication, ConceptNote, IConceptNote } from '@/entities'
import { ActionTree } from 'vuex';
import { AppConstants as _app } from "@/shared/app.constants";


export const actions: IActions = {
    /** set the initiative call */
    setCallId(context: Context, id: number): any {
        context.commit('setSelectedCall', id);
    },
    /** create new Concept note */
    createConceptNote(context: Context, payload: number): any {
        let title = localStorage.getItem("language") === "en-US" ? 'New Concept Note' : 'Nouvelle Demande';
        const concept = new ConceptNote({ InitiativeCallId: payload, Title: title } as IConceptNote);

        return this.$services.conceptNote.createConceptNote(concept)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    context.commit('setConceptNote', { conceptNote: data.Record });
                    context.commit('setSelectedConceptNote', data.Record.ConceptNoteId);
                    return data.Record.ConceptNoteId;
                } else {
                    context.commit('setNotification', new Notification({ snack: true, text: data.Message, color: 'error' } as INotification))
                    return null;
                }
            }).catch((e) => {
                context.commit('setNotification', new Notification({ snack: true, text: e, color: 'error' } as INotification))
            });
    },

    /** save the application data (ES) */
    saveAppData(context: Context, payload: ISaveConceptNotePayload): any {
        return this.$services.conceptNote.updateTechnicalApplication(payload.id, this.$services.application.transformAppData(payload.data), payload.section)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    data = this.$services.application.transformDBEntity(data.Record, context.getters.rootTemplate, context.getters.isRAGTemplate, context.getters.indicators);
                    context.commit('setAppData', data);
                    return true;
                } else {
                    context.commit('setNotification', new Notification({ snack: true, isHtml: false, text: data.Message, color: 'error' } as INotification))
                    return false;
                }
            });
    },

    /** save the application data (ES) on form is dirty */
    saveApplicationData(context: Context, payload: ISaveConceptNotePayload): any {
        context.commit('updateOverlay', true);
        return this.$services.conceptNote.updateTechnicalApplication(payload.id, this.$services.application.transformAppData(payload.data), payload.section)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    var errMsg = data.errMessages ? data.errMessages : []
                    data = this.$services.application.transformDBEntity(data.Record, context.getters.rootTemplate, context.getters.isRAGTemplate, context.getters.indicators);
                    context.commit('setAppData', data);
                    this.$services.conceptNote.updateApplication(payload.id, context.state.conceptNote);
                    context.commit('updateOverlay', false);
                    return { success: true, data: data, errMsg: errMsg };
                } else {
                    context.commit('updateOverlay', false);
                    context.commit('setNotification', new Notification({ snack: true, isHtml: false, text: data.Message, color: 'error' } as INotification))
                    return { success: false, data: data, errMsg: errMsg };
                }
            })
            .catch((e) => {
                context.commit('updateOverlay', false);
                context.commit('setNotification', new Notification({ snack: true, isHtml: false, text: e, color: 'error' } as INotification))
                return false;
            });
    },

    saveConceptNote(context: Context, payload: ISaveConceptNotePayload): any {
        if (payload.context === 'submit') {
            // TO DO: Workflow implementation
            if (payload.data.WorkFlowStatusId)
                payload.data.WorkFlowStatusId = payload.data.WorkFlowStatusId + 1;
        }
        return this.$services.conceptNote.updateApplication(payload.id, payload.data)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    context.commit('setConceptNote', { conceptNote: data.Record });
                    switch (payload.context) {
                        case 'submit':
                            // context.commit('setNotification', new Notification({ snack: true, text: 'Application submitted successfully' } as INotification))
                            break;

                        case 'workflowAction':
                            context.commit('setNotification', new Notification({ snack: true, text: 'Application status changed successfully' } as INotification))
                            break;

                        default:
                            context.commit('setNotification', new Notification({ snack: true, text: 'Application saved successfully' } as INotification))
                            break;
                    }
                    return true;
                } else {
                    if (data.ValidationMessages && data.ValidationMessages.length > 0) {
                        context.commit('setValidationMessages', data.ValidationMessages);
                        return false;
                    } else {
                        context.commit('setNotification', new Notification({ snack: true, isHtml: true, text: data.Message, color: 'error' } as INotification));
                        return false;
                    }
                }
            });
    },

    /** update the state while switch the language */
    updateLanguage(context: Context, payload: string): void {
        context.commit('setLanguage', payload)
        context.commit('refreshTemplates')
    },

    /** get the application header data */
    getConceptNote(context: Context, id: number): any {
        return this.$services.conceptNote.getConceptNote(id)
            .then((data: any) => {
                if (data.Checklist) {
                    const lang = (localStorage.getItem("language") || "en-US").split('-')[0];
                    data.Checklist = this.$services.application.transformAppChecklist(data.Checklist, "x-order", lang);
                }

                context.commit('setChecklist', data.Checklist);

                //set the log framework 
                let logFrameworkSchema = _app.logicalFrameworkHeaders;
                if (logFrameworkSchema) {
                    const lang = (localStorage.getItem("language") || "en-US").split('-')[0];
                    let transformedLogFramework = this.$services.application.transformLogFramworkSchema(logFrameworkSchema, lang);
                    context.commit('setLogSchema', transformedLogFramework);
                }

                // set the template
                if (data.Template) {
                    let tmpl = data.Template.reduce((obj: any, tmpl: any) => {
                        obj[tmpl.Key] = tmpl.Value;
                        return obj;
                    }, {});
                    context.commit('setTemplate', { templates: tmpl });
                    context.commit('refreshTemplates');
                }

                // set the ConceptNote
                if (data.ConceptNote)
                    context.commit('setConceptNote', { conceptNote: data.ConceptNote });

                // set the application data
                if (data.ConceptNoteData)
                    context.commit('setAppData', data.ConceptNoteData);

                // set the negotiation log
                if (data.Indicators)
                    context.commit('setIndicators', data.Indicators);

                return data.ConceptNoteData;

            })
            .catch((e) => {
                context.commit('setNotification', new Notification({ snack: true, text: e, color: 'error' } as INotification))
            });
    },

    updatePPO(context: Context, payload: any): any {
        return this.$services.conceptNote.updatePPO(payload.id, payload.ppoid)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    context.commit('setPPO', { data: data.Record });
                    context.commit('application/setNotification', new Notification({ snack: true, text: data.Message, color: 'success' } as INotification), { root: true });
                } else {
                    context.commit('application/setNotification', new Notification({ snack: true, isHtml: true, text: data.Message, color: 'error' } as INotification), { root: true })
                }
            });
    },

    changeOwnership(context: Context, payload: any): any {
        return this.$services.conceptNote.changeOwnership(payload.id, payload.userId, 'conceptnote')
            .then((data: any) => {
                if (data.Result === 'OK') {
                    // context.commit('setGrantee', { data: data.Record });
                    context.commit('application/setNotification', new Notification({ snack: true, text: data.Message, color: 'success' } as INotification), { root: true });
                } else {
                    context.commit('application/setNotification', new Notification({ snack: true, isHtml: true, text: data.Message, color: 'error' } as INotification), { root: true })
                }
            });
    },

    /** update the sub menu */
    updateSubMenu(context: Context, payload: any): void {
        context.commit('setSubMenu', payload)
    },
    /** get the application header data */
    getApplication(context: Context, id: number): any {
        return this.$services.conceptNote.getConceptNote(id)
            .then((data: any) => {
                if (data.Checklist) {
                    const lang = (localStorage.getItem("language") || "en-US").split('-')[0];
                    data.Checklist = this.$services.application.transformAppChecklist(data.Checklist, "x-order", lang);
                }

                context.commit('setChecklist', data.Checklist);

                // set the template
                if (data.Template) {
                    let tmpl = data.Template.reduce((obj: any, tmpl: any) => {
                        obj[tmpl.Key] = tmpl.Value;
                        return obj;
                    }, {});
                    context.commit('setTemplate', { templates: tmpl });
                    context.commit('refreshTemplates');
                }

                // set the application
                if (data.ConceptNote)
                    context.commit('setApplication', { conceptNote: data.ConceptNote });

                // set the application data
                if (data.ApplicationData)
                    context.commit('setAppData', data.ApplicationData);

                // set the negotiation log
                if (data.Indicators)
                    context.commit('setIndicators', data.Indicators);

                return data.ApplicationData;

            })
            .catch((e) => {
                context.commit('setNotification', new Notification({ snack: true, text: e, color: 'error' } as INotification))
            });
    },

    /** update the state with workflow status  */
    getConceptNoteStatus(context: Context, id: number): any {
        if (id) {
            return this.$services.conceptNote.getApplicationData(id)
                .then((data: any) => {
                    context.commit('setApplication', { conceptNote: data });
                });
        }
    },

    /** reset the application state when navigate to dashboard */
    resetAppState(context: Context): any {
        context.commit('resetAppState');
    },
    /** set the Concept Note id */
    setConceptNoteId(context: Context, id: number): any {
        context.commit('setSelectedConceptNote', id);
    },
    /** update the top menu */
    updateTopMenu(context: Context, payload: string): void {
        context.commit('setTopMenu', payload)
    },
    resetTabs(context: Context) {
        context.commit('resetActiveTabs');
    },
    /** upload the document */
    uploadDocument(context: Context, payload: any) {
        context.commit('updateOverlay', true);
        return this.$services.document.uploadDocument(payload.file, payload.schema, context.state.conceptNote.ConceptNoteId, payload.language, false, _app.menu.conceptNote)
            .then((resp) => {
                context.commit('updateOverlay', false);
                return resp;
            }).catch((e) => {
                context.commit('updateOverlay', false);
                context.commit('setNotification', new Notification({ snack: true, text: e, color: 'error' } as INotification));
            })
    },

    /** update the project title */
    updateProjectTitle(context: Context, payload: string): void {
        context.commit('setTitle', payload)
    },

    createCustomIndicator(context: Context, payload: any): void {
        return this.$services.conceptNote.createCustomIndicator(payload.data, payload.id)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    context.commit('addIndicator', data.Record);
                } else {
                    let spliter = "</br>",
                        errors = "",
                        messages = data.Message.split(spliter);

                    for (var i = 0; i < messages.length; i++) {
                        var message = messages[i].split("@#");
                        if (message && message[1]) {
                            errors += (i + 1).toString() + ". " + message[1] + spliter;
                        }
                    }
                    context.commit('setNotification', new Notification({ snack: true, isHtml: true, text: errors, color: 'error' } as INotification))
                }

                return data;
            });
    },

    updateCustomIndicator(context: Context, payload: any): void {
        return this.$services.conceptNote.updateCustomIndicator(payload.data, payload.id)
            .then((data: any) => {
                if (data.Result === 'OK') {
                    context.commit('updateIndicator', data.Record);
                    return data.Record;
                } else {
                    context.commit('setNotification', new Notification({ snack: true, isHtml: false, text: data.Message, color: 'error' } as INotification))
                }
            });
    },

    deleteCustomIndicator(context: Context, payload: any): void {

    },

    importBudgetFile(context: Context, payload: any) {
        context.commit('updateOverlay', true);
        return this.$services.budget.importFile(payload.file, payload.transactionId, payload.schema, payload.context)
            .then((resp) => {
                context.commit('updateOverlay', false);
                if (resp.Result === 'ERROR')
                    context.commit('setNotification', new Notification({ snack: true, text: resp.Message, color: 'error' } as INotification));
                else
                    return resp;
            }).catch((e) => {
                context.commit('updateOverlay', false);
                context.commit('setNotification', new Notification({ snack: true, text: e, color: 'error' } as INotification));
            });
    },

    getSnapshot(context: Context, payload: any): any {
        if (payload.applicationId) {
            return this.$services.conceptNote.getIndicators(payload.applicationId)
                .then((data: any) => {
                    context.commit('setIndicators', data);
                    return this.$services.conceptNote.getApplicationData(payload.applicationId)
                        .then((data: any) => {
                            context.commit('setApplication', { conceptNote: data });
                            return this.$services.conceptNote.getTemplate(data.InitiativeCallId)
                                .then((tmpl: any) => {
                                    context.commit('setTemplate', { templates: tmpl });
                                })
                                .then(() => {
                                    return this.$services.conceptNote.getSnapshot(payload.workflowLogId)
                                        .then((data: any) => {
                                            if (data.Checklist) {
                                                const lang = (localStorage.getItem("language") || "en-US").split('-')[0];
                                                data.Checklist = this.$services.application.transformAppChecklist(data.Checklist, "x-order", lang);
                                            }

                                            context.commit('setChecklist', data.Checklist);

                                             //set the log framework 
                                            let logFrameworkSchema = _app.logicalFrameworkHeaders;
                                            if (logFrameworkSchema) {
                                                const lang = (localStorage.getItem("language") || "en-US").split('-')[0];
                                                let transformedLogFramework = this.$services.application.transformLogFramworkSchema(logFrameworkSchema, lang);
                                                context.commit('setLogSchema', transformedLogFramework);
                                            }

                                            context.commit('refreshTemplates');

                                            data = this.$services.application.transformDBEntity(data.ConceptNoteData, context.getters.rootTemplate, context.getters.isRAGTemplate, context.getters.indicators);
                                            context.commit('setAppData', data);
                                            return data;
                                        });
                                })
                        });
                });
        }
    }
}